import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableCell,
  TableRow,
  WidthType,
  AlignmentType,
  BorderStyle,
  Header,
  Footer,
  PageNumber,
  ImageRun,
} from "docx";
import { saveAs } from "file-saver";

const base64ToBuffer = (base64Str) => {
  const parts = base64Str.split(";base64,");
  const raw = window.atob(parts[1]);
  const rawLength = raw.length;
  const array = new Uint8Array(new ArrayBuffer(rawLength));
  for (let i = 0; i < rawLength; i++) {
    array[i] = raw.charCodeAt(i);
  }
  return array;
};

export const exportWorkspaceAsDocx = async ({
  entityName,
  entityCode,
  entityType,
  today,
  metadata,
  scopeData,
  lcaData,
  processes = [],
  footprintData,
  envFactors = [],
  logoBase64 = null,
}) => {
  const headingStyle = (text, options = {}) => {
    return new Paragraph({
      children: [
        new TextRun({
          text,
          bold: true,
          color: "15A448", // Arivu Green
          font: "Poppins",
          size: 26,
        }),
      ],
      spacing: { before: 240, after: 120 },
      ...options,
    });
  };

  const bodyStyle = (text, options = {}) => {
    return new Paragraph({
      children: [
        new TextRun({
          text,
          font: "Poppins",
          size: 20,
          color: "374151",
          ...options,
        }),
      ],
      spacing: { after: 120 },
    });
  };

  const createTable = (headers, rowsData) => {
    const headerRow = new TableRow({
      children: headers.map(
        (header) =>
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: String(header),
                    bold: true,
                    color: "FFFFFF",
                    font: "Poppins",
                    size: 20,
                  }),
                ],
                alignment: AlignmentType.LEFT,
              }),
            ],
            shading: { fill: "15A448" },
            margins: { top: 120, bottom: 120, left: 150, right: 150 },
          })
      ),
    });

    const dataRows = rowsData.map((row, idx) =>
      new TableRow({
        children: row.map(
          (cell) =>
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: String(cell ?? ""),
                      font: "Poppins",
                      size: 20,
                      color: "171717",
                    }),
                  ],
                  alignment: AlignmentType.LEFT,
                }),
              ],
              shading: { fill: idx % 2 === 0 ? "F3FBF5" : "FFFFFF" },
              margins: { top: 100, bottom: 100, left: 150, right: 150 },
            })
        ),
      })
    );

    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [headerRow, ...dataRows],
      borders: {
        top: { style: BorderStyle.SINGLE, size: 4, color: "E5E7EB" },
        bottom: { style: BorderStyle.SINGLE, size: 4, color: "E5E7EB" },
        left: { style: BorderStyle.SINGLE, size: 4, color: "E5E7EB" },
        right: { style: BorderStyle.SINGLE, size: 4, color: "E5E7EB" },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: "F3F4F6" },
        insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      },
    });
  };

  // NATIVE BAR GRAPH REPRESENTATION
  const createBarFillRow = (percent, color) => {
    const filled = Math.max(1, Math.round((percent / 100) * 20));
    const cells = [];

    for (let index = 0; index < 20; index += 1) {
      cells.push(
        new TableCell({
          children: [new Paragraph({ text: "", spacing: { after: 0 } })],
          shading: { fill: index < filled ? color : "E5E7EB" },
          margins: { top: 30, bottom: 30, left: 0, right: 0 },
          width: { size: 5, type: WidthType.PERCENTAGE },
        })
      );
    }

    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [new TableRow({ children: cells })],
      borders: {
        top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        insideHorizontal: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
        insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      },
    });
  };

  const createHorizontalBarChartTable = (title, labels, values, options = {}) => {
    const maxValue = Math.max(...values, 1);
    const rows = labels.map((label, index) => {
      const value = Number(values[index] || 0);
      const percent = (value / maxValue) * 100;
      return new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: String(label),
                    font: "Poppins",
                    size: 18,
                    color: "111827",
                  }),
                ],
                spacing: { after: 0 },
              }),
            ],
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            width: { size: 40, type: WidthType.PERCENTAGE },
          }),
          new TableCell({
            children: [
              createBarFillRow(percent, options.barColor || "15A448"),
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                spacing: { before: 40, after: 0 },
                children: [
                  new TextRun({
                    text: `${value.toFixed(2)} kg CO₂e`,
                    font: "Poppins",
                    size: 16,
                    color: "374151",
                    bold: true,
                  }),
                ],
              }),
            ],
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            width: { size: 60, type: WidthType.PERCENTAGE },
          }),
        ],
      });
    });

    return new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: title,
                      font: "Poppins",
                      size: 22,
                      bold: true,
                      color: "111827",
                    }),
                  ],
                  spacing: { after: 80 },
                }),
              ],
              columnSpan: 2,
              shading: { fill: "FFFFFF" },
              borders: {
                top: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                bottom: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                left: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
                right: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
              },
            }),
          ],
        }),
        ...rows,
      ],
      borders: {
        top: { style: BorderStyle.SINGLE, size: 4, color: "E5E7EB" },
        bottom: { style: BorderStyle.SINGLE, size: 4, color: "E5E7EB" },
        left: { style: BorderStyle.SINGLE, size: 4, color: "E5E7EB" },
        right: { style: BorderStyle.SINGLE, size: 4, color: "E5E7EB" },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: "F3F4F6" },
        insideVertical: { style: BorderStyle.NONE, size: 0, color: "FFFFFF" },
      },
    });
  };

  // Compile calculations for stages (checking startsWith since database has descriptive stage names)
  const stageLabels = ["A1: Raw Material Acquisition", "A2: Transport to Factory", "A3: Manufacturing"];
  const stageValues = ["A1", "A2", "A3"].map((stage) => {
    return processes
      .filter((p) => p.lca_stage && p.lca_stage.startsWith(stage))
      .reduce((sum, p) => sum + (parseFloat(p.gwp) || 0), 0);
  });

  const children = [
    // PAGE 1: COVER PAGE
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 2400 },
      children: [
        new TextRun({
          text: entityName.toUpperCase(),
          bold: true,
          font: "Poppins",
          size: 40,
          color: "15A448",
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 3600 },
      children: [
        new TextRun({
          text: `${entityType.toUpperCase()} LIFE CYCLE ASSESSMENT REPORT`,
          bold: true,
          font: "Poppins",
          size: 20,
          color: "4B5563",
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 1200 },
      children: [
        new TextRun({
          text: `Report Generated on: ${today}`,
          font: "Poppins",
          size: 20,
          color: "4B5563",
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 240 },
      children: [
        new TextRun({
          text: `Generated by Arivu LCA Engine`,
          bold: true,
          font: "Poppins",
          size: 20,
          color: "15A448",
        }),
      ],
    }),

    // PAGE 2: TABLE OF CONTENTS
    headingStyle("TABLE OF CONTENTS", { pageBreakBefore: true }),
    bodyStyle("1. Workspace & Entity Overview ........................................................................................ Page 3"),
    bodyStyle("2. Scope & Goals .............................................................................................................. Page 4"),
    bodyStyle("3. Life Cycle Inventory (LCI) Processes ............................................................................ Page 5"),
    bodyStyle("4. Environmental Factors Mapped ........................................................................................ Page 6"),
    bodyStyle("5. Environmental Footprint & Impact Results ........................................................................ Page 7"),

    // PAGE 3: WORKSPACE & ENTITY OVERVIEW
    headingStyle("1. Workspace & Entity Overview", { pageBreakBefore: true }),
    bodyStyle("General profile details describing the evaluated workspace:"),
    createTable(
      ["Attribute", "Value"],
      [
        ["Entity Name", entityName],
        ["Product UUID", entityCode],
        ["Entity Type", entityType],
        ["Category", metadata.category || "N/A"],
        ["Sub-Category", metadata.sub_category || "N/A"],
        ["Dimension / Specifications", metadata.specifications || "N/A"],
        ["Reference Service Life", metadata.serviceLife || "N/A"],
        ["Extended Service Life", metadata.extendedServiceLife || "N/A"],
        ["Place of Manufacture", metadata.placeOfManufacture || "N/A"],
        ["Supplier / Organization Name", metadata.organizationName || "N/A"],
        ["Contact Person", metadata.contactPerson || "N/A"],
        ["Contact Email", metadata.contactEmail || "N/A"],
        ["Contact Phone", metadata.contactPhone || "N/A"],
        ["Supplier Address", metadata.supplierAddress || "N/A"],
        ["Supplier Website", metadata.supplierWebsite || "N/A"],
      ]
    ),

    // PAGE 4: SCOPE & GOALS
    headingStyle("2. Scope & Goals", { pageBreakBefore: true }),
    bodyStyle("LCA evaluation scope boundaries, goals, and organizational structures:"),
    createTable(
      ["Goal / Boundary Parameter", "Configured Setting"],
      [
        ["User Type / Role", scopeData.role || "N/A"],
        ["Organizational Boundary", scopeData.boundary || "Operational control"],
        ["Temporal Boundary / Coverage", scopeData.temporal || "N/A"],
        ["Reporting Cut-off Rule", scopeData.cutoff || "N/A"],
        ["Boundary Statement", scopeData.boundary_statement || "N/A"],
        ["Goals of Assessment", scopeData.goals || "N/A"],
        ["Assumptions & Cut-offs", scopeData.assumptions || "N/A"],
        ["Declared Unit", lcaData.declared_unit || "1 unit"],
        ["Reference PCR", lcaData.reference_pcr || "N/A"],
        ["LCA Software", lcaData.lca_software || "N/A"],
        ["LCI Database", lcaData.lci_database || "N/A"],
        ["LCIA Methodology", lcaData.lcia_methodology || "N/A"],
        ["Cut-Off Parameters", lcaData.cutoff || "N/A"],
        ["Allocation Rules / Procedure", lcaData.allocation_procedure || "N/A"],
        ["System Boundary", scopeData.boundary_statement || "N/A"],
      ]
    ),

    // PAGE 5: LIFE CYCLE INVENTORY (LCI) PROCESSES
    headingStyle("3. Life Cycle Inventory (LCI) Processes", { pageBreakBefore: true }),
    bodyStyle("Inventory processes mapped to their respective life cycle stages:"),
    createTable(
      ["Process Name", "LCA Stage", "GWP Total", "Biogenic GWP"],
      processes.map((p) => [
        p.name || "N/A",
        p.lca_stage || "N/A",
        p.gwp != null ? Number(p.gwp).toFixed(2) : "0.00",
        p.biogenic_gwp != null ? Number(p.biogenic_gwp).toFixed(2) : "0.00",
      ])
    ),
    new Paragraph({ spacing: { before: 240, after: 120 } }),
    bodyStyle("Process Summary Statement:", { bold: true }),
    new Paragraph({
      spacing: { after: 120 },
      bullet: { level: 0 },
      children: [
        new TextRun({
          text: `For the mapped life cycle processes, the total Global Warming Potential (fossil GWP) is ${footprintData.fossilGwp.toFixed(2)} kg CO₂e.`,
          font: "Poppins",
          size: 20,
        })
      ]
    }),
    new Paragraph({
      spacing: { after: 120 },
      bullet: { level: 0 },
      children: [
        new TextRun({
          text: `The total biogenic carbon (biogenic GWP) is ${footprintData.biogenicGwp.toFixed(2)} kg CO₂e.` ,
          font: "Poppins",
          size: 20,
        })
      ]
    }),
    new Paragraph({
      spacing: { after: 120 },
      bullet: { level: 0 },
      children: [
        new TextRun({
          text: `The net cumulative impact results in a total carbon footprint of ${footprintData.netGwp.toFixed(2)} kg CO₂e.`,
          font: "Poppins",
          size: 20,
        })
      ]
    }),

    // PAGE 6: ENVIRONMENTAL FACTORS
    headingStyle("4. Environmental Factors Mapped", { pageBreakBefore: true }),
    bodyStyle("List of background environmental factors contributing to the process footprints:"),
    createTable(
      ["Category", "Sub-Category", "Quantity", "Unit", "GWP Total"],
      envFactors.map((f) => [
        f.category || "N/A",
        f.sub_category || "N/A",
        f.quantity != null ? String(f.quantity) : "0",
        f.unit || "N/A",
        f.gwp_total != null ? Number(f.gwp_total).toFixed(2) : "0.00",
      ])
    ),

    // PAGE 7: RESULTS (CHARTS & BOUNDARY IMAGES)
    headingStyle("5. Environmental Footprint & Impact Results", { pageBreakBefore: true }),
    bodyStyle("Global Warming Potential breakdown and life cycle stage impacts:"),

    // Native bar chart representation table
    createHorizontalBarChartTable(
      "GWP by LCA Stage",
      stageLabels,
      stageValues
    ),
    new Paragraph({ spacing: { before: 240, after: 240 } }),

    // Native System Boundary Grid Table (fully horizontal, avoiding text rotation issues)
    bodyStyle("System Boundary Stages Summary", { bold: true }),
    createTable(
      ["LCA Stage", "Stage Name", "GWP Impact (kg CO₂e)"],
      ["A1", "A2", "A3"].map((stage, idx) => [
        stage,
        stage === "A1" ? "Raw Material Acquisition" : stage === "A2" ? "Transport to Factory" : "Manufacturing",
        stageValues[idx].toFixed(2),
      ])
    ),
  ];

  // Define headers and footers (with increased top-margin and larger non-shrinked logo)
  const header = logoBase64
    ? new Header({
        children: [
          new Paragraph({
            children: [
              new ImageRun({
                data: base64ToBuffer(logoBase64),
                transformation: {
                  width: 130,
                  height: 36,
                },
              }),
            ],
            spacing: { after: 200 },
          }),
        ],
      })
    : undefined;

  const footer = new Footer({
    children: [
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        children: [
          new TextRun({
            children: ["Page ", PageNumber.CURRENT, " of ", PageNumber.TOTAL_PAGES],
            font: "Poppins",
            size: 16,
            color: "6B7280",
          }),
        ],
      }),
    ],
  });

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 1800, // increased top margin to prevent overlapping with larger logo
              bottom: 1440,
              left: 1440,
              right: 1440,
              header: 720,
              footer: 720,
            }
          }
        },
        headers: header ? { default: header } : undefined,
        footers: { default: footer },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${entityName.replace(/\s+/g, "_")}_LCA_Report.docx`);
};
